import isEmpty from 'lodash/isEmpty';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { RouteChangeAnalyticsParams } from '../../store/analyticStore/types/RouteChangeAnalyticsParams';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import { replaceUUID } from './functions/replaceUUID';

const RouteChangeGuard = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const location = useLocation();
  const { sendLandedWithParametersAnalytics, sendRouteChangeAnalytics } =
    useAnalyticStore();

  const previousLocation = useRef<string | null>(null);

  useEffect((): void => {
    const fromRouteUrl = previousLocation.current ?? '';
    const fromRouteUrlTrimmed = fromRouteUrl ? replaceUUID(fromRouteUrl) : '';

    const toRouteUrl =
      window.location.origin +
      location.pathname +
      location.search +
      location.hash;
    const toRouteUrlTrimmed = replaceUUID(toRouteUrl);

    // Prevent analytics for non-route changes
    // that could be caused by re-renders or react lifecycle events
    if (fromRouteUrl && fromRouteUrl === toRouteUrl) {
      return;
    }

    const params: RouteChangeAnalyticsParams = {
      fromRouteUrl,
      fromRouteUrlTrimmed,
      toRouteUrl,
      toRouteUrlTrimmed,
    };

    sendRouteChangeAnalytics(params);

    // Update the previous location for next route change
    previousLocation.current = toRouteUrl;
  }, [location.key]);

  useEffect((): void => {
    const searchParams = new URLSearchParams(window.location.search);
    const entries = Object.fromEntries(searchParams.entries());

    if (isEmpty(entries)) {
      return;
    }

    sendLandedWithParametersAnalytics(
      window.location.href,
      window.location.host,
      window.location.pathname,
      entries,
    );
  }, []);

  return children;
};

export default RouteChangeGuard;
